import { FC, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import styles from "./FormImportFile.module.scss";
import cn from "classnames";
import ModalFormWindow from "@/HOC/ModalFormWindow/ModalFormWindow";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colorRed } from "@/constants/colors";
import { useTranslate } from "@/hooks";
import NoticeSingleAction from "@/ui/NoticeSingleAction/NoticeSingleAction";
import { acceptedFilesTypography } from "./FormImportFile.const";
import { IFormImportFile } from "./FormImportFile.props";
import { CustomButton } from "@/ui";

const FormImportFile: FC<IFormImportFile> = ({ showModal, setShowModal, onSubmit, title, allowedTypes, ...props }) => {
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [dragOver, setDragOver] = useState(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [showNotification, setShowNotification] = useState(false);
	const [notificationTitle, setNotificationTitle] = useState("");
	const translate = useTranslate();

	const acceptString = allowedTypes.map((t) => t.ext).join(",");

	const showError = (msg: string) => {
		setNotificationTitle(msg);
		setShowNotification(true);
	};

	const validateFile = (file: File): boolean => {
		const ext = file.name.toLowerCase().substring(file.name.lastIndexOf("."));

		const isValid = allowedTypes.some((t) => file.type === t.mime || ext === t.ext.toLowerCase());

		if (!isValid) {
			const typesStr = allowedTypes.map((t) => t.ext).join(", ");
			showError(`Можно загружать только файлы: ${typesStr}`);
		}

		return isValid;
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setDragOver(false);

		const file = e.dataTransfer.files[0];
		if (file && validateFile(file)) {
			setSelectedFile(file);
		}
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			const file = e.target.files[0];
			if (validateFile(file)) {
				setSelectedFile(file);
			} else {
				e.target.value = "";
			}
		}
	};

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setDragOver(true);
	};

	const handleDragLeave = () => {
		setDragOver(false);
	};

	const handleClick = () => {
		inputRef.current?.click();
	};

	const handleClose = () => {
		setShowModal(false);
	};

	const handleSubmit = () => {
		if (selectedFile) {
			onSubmit(selectedFile);
			handleClose();
			handleClear();
		}
	};

	const handleClear = () => {
		setSelectedFile(null);
		if (inputRef.current) inputRef.current.value = "";
	};

	return (
		<>
			<ModalFormWindow showModal={showModal} setShowModal={setShowModal} headerTitle={title} close={handleClose}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
					<div
						onDrop={handleDrop}
						onDragOver={handleDragOver}
						onClick={handleClick}
						onDragLeave={handleDragLeave}
						className={cn(styles.dropZone, { [styles.dragOver]: dragOver })}
					>
						<Box display="flex" alignItems="center" justifyContent="space-around" width="100%">
							<Typography variant="body2" sx={{ fontSize: 13 }} className={styles.dropText}>
								{selectedFile?.name || translate(acceptedFilesTypography["all"])}
							</Typography>
							{selectedFile && (
								<FontAwesomeIcon
									onClick={(e) => {
										e.stopPropagation();
										handleClear();
									}}
									icon={faTrashCan}
									color={colorRed}
								/>
							)}
						</Box>
						<input ref={inputRef} type="file" accept={acceptString} style={{ display: "none" }} onChange={handleFileChange} />
					</div>
					{props.children}

					<CustomButton fullWidth onClick={handleSubmit} disabled={!selectedFile}>
						{translate("button_load")}
					</CustomButton>
				</Box>
			</ModalFormWindow>

			<NoticeSingleAction showNotification={showNotification} setShowNotification={setShowNotification} title={notificationTitle} severity="error" />
		</>
	);
};

export default FormImportFile;
